const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]):not([tabindex="-1"]), [data-enter-focus]:not([disabled])';

function isElementDisabled(el: HTMLElement): boolean {
  if ('disabled' in el && (el as HTMLInputElement).disabled) return true;
  if (el.getAttribute('aria-disabled') === 'true') return true;
  return el.closest('[data-enter-nav-disabled="true"]') != null;
}

function resolveFocusTarget(node: HTMLElement): HTMLElement | null {
  if (node.matches(FOCUSABLE_SELECTOR)) return node;
  const inner = node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
  return inner ?? node;
}

export function getEnterNavFocusables(root: ParentNode): HTMLElement[] {
  const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-enter-nav]'));
  const seen = new Set<HTMLElement>();
  const focusables: HTMLElement[] = [];

  for (const node of nodes) {
    if (node.getAttribute('data-enter-nav-disabled') === 'true') continue;
    const target = resolveFocusTarget(node);
    if (!target || isElementDisabled(target) || seen.has(target)) continue;
    seen.add(target);
    focusables.push(target);
  }

  return focusables;
}

export function focusNextEnterNav(current: HTMLElement | null | undefined): boolean {
  if (!current) return false;
  const root = current.closest('[data-enter-nav-root]');
  if (!root) return false;

  const focusables = getEnterNavFocusables(root);
  const navNode = current.closest('[data-enter-nav]') as HTMLElement | null;
  const currentFocus = resolveFocusTarget(navNode ?? current) ?? current;

  let index = focusables.findIndex(
    (el) => el === currentFocus || el.contains(currentFocus) || currentFocus.contains(el),
  );
  if (index < 0) {
    index = focusables.findIndex((el) => navNode != null && (el === navNode || navNode.contains(el)));
  }

  const next = index >= 0 ? focusables[index + 1] : focusables[0];
  if (!next) return false;

  next.focus();
  return true;
}

export function handleEnterNavKeyDown(
  event: React.KeyboardEvent,
  current?: HTMLElement | null,
): boolean {
  if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return false;
  event.preventDefault();
  return focusNextEnterNav(current ?? (event.currentTarget as HTMLElement));
}
